import React, { useState } from "react";
import { API_ENDPOINTS } from "../../config/api";
import { useAuth } from "../../context/AuthContext";
import { TT_AREA_GROUPS } from "../../config/ttAreas";

// ── Modal Styles ───────────────────────────────────────────────────────────────
const ms = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.45)",
    backdropFilter: "blur(2px)",
    zIndex: 1060,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "1rem",
  },
  modal: {
    background: "#fff",
    borderRadius: 12,
    width: "100%",
    maxWidth: 700,
    maxHeight: "90vh",
    display: "flex",
    flexDirection: "column",
    boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
  },
  modalHead: {
    padding: "1.1rem 1.4rem",
    borderBottom: "1px solid #e5e7eb",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexShrink: 0,
  },
  modalTitle: { fontSize: 16, fontWeight: 600, color: "#111827" },
  closeBtn: {
    background: "none",
    border: "none",
    fontSize: 20,
    lineHeight: 1,
    cursor: "pointer",
    color: "#6b7280",
    padding: "0 4px",
  },
  modalBody: {
    padding: "1.25rem 1.4rem",
    overflowY: "auto",
    flex: 1,
    fontFamily:
      "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    fontSize: 13,
    color: "#374151",
  },
  modalFoot: {
    padding: "0.9rem 1.4rem",
    borderTop: "1px solid #e5e7eb",
    display: "flex",
    justifyContent: "flex-end",
    gap: 8,
    flexShrink: 0,
  },
  sectionHead: {
    fontSize: 13,
    fontWeight: 600,
    color: "#2563EB",
    borderBottom: "1px solid #e5e7eb",
    paddingBottom: 8,
    marginBottom: 12,
    marginTop: 8,
  },
  grid2: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "0.85rem",
  },
  gridFull: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: "0.85rem",
  },
  grid3: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr",
    gap: "0.85rem",
  },
  field: { display: "flex", flexDirection: "column", gap: 4 },
  label: { fontSize: 12, fontWeight: 500, color: "#374151" },
  input: {
    padding: "7px 10px",
    borderRadius: 6,
    border: "1px solid #d1d5db",
    fontSize: 13,
    color: "#111827",
    background: "#fff",
    outline: "none",
    width: "100%",
    boxSizing: "border-box",
  },
  select: {
    padding: "7px 10px",
    borderRadius: 6,
    border: "1px solid #d1d5db",
    fontSize: 13,
    color: "#111827",
    background: "#fff",
    outline: "none",
    width: "100%",
    boxSizing: "border-box",
  },
  textarea: {
    padding: "7px 10px",
    borderRadius: 6,
    border: "1px solid #d1d5db",
    fontSize: 13,
    color: "#111827",
    background: "#fff",
    outline: "none",
    width: "100%",
    boxSizing: "border-box",
    resize: "vertical",
    minHeight: 72,
  },
  errorBanner: {
    fontSize: 13,
    color: "#991B1B",
    background: "#FEF2F2",
    border: "1px solid #FECACA",
    borderRadius: 8,
    padding: "10px 14px",
    marginBottom: "1rem",
  },
  btnPrimary: {
    fontSize: 13,
    fontWeight: 500,
    padding: "7px 18px",
    borderRadius: 7,
    border: "none",
    background: "#2563EB",
    color: "#fff",
    cursor: "pointer",
  },
  btnSecondary: {
    fontSize: 13,
    fontWeight: 500,
    padding: "7px 18px",
    borderRadius: 7,
    border: "1px solid #d1d5db",
    background: "transparent",
    color: "#374151",
    cursor: "pointer",
  },
  spinner: {
    display: "inline-block",
    width: 13,
    height: 13,
    border: "2px solid rgba(255,255,255,0.4)",
    borderTopColor: "#fff",
    borderRadius: "50%",
    animation: "spin 0.7s linear infinite",
    verticalAlign: "middle",
    marginRight: 6,
  },
};

// ── Helpers ────────────────────────────────────────────────────────────────────
const Field = ({ label, children, span2 = false, span3 = false }) => (
  <div style={{ ...ms.field, ...(span2 ? { gridColumn: "1 / -1" } : {}), ...(span3 ? { gridColumn: "1 / -1" } : {}) }}>
    <label style={ms.label}>{label}</label>
    {children}
  </div>
);

// ── Component ──────────────────────────────────────────────────────────────────
function AddTenantModal({ show, handleClose, onTenantAdded }) {
  const { getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    personalInfo: {
      firstName: "", lastName: "", email: "", phone: "", dateOfBirth: "",
    },
    employment: {
      employer: "", jobTitle: "", monthlyIncome: "", employmentStartDate: "", workPhone: "", workEmail: "",
    },
    currentAddress: {
      street: "", city: "", parish: "", zipCode: "", moveInDate: "", reasonForMoving: "",
    },
    emergencyContact: {
      name: "", relationship: "", phone: "", email: "",
    },
    status: "prospective",
    notes: "",
  });

  const set = (section, field, value) =>
    setFormData((prev) => ({ ...prev, [section]: { ...prev[section], [field]: value } }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(API_ENDPOINTS.tenants.create, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify(formData),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Error: ${response.status}`);
      }
      const data = await response.json();

      setFormData({
        personalInfo: { firstName: "", lastName: "", email: "", phone: "", dateOfBirth: "" },
        employment: { employer: "", jobTitle: "", monthlyIncome: "", employmentStartDate: "", workPhone: "", workEmail: "" },
        currentAddress: { street: "", city: "", parish: "", zipCode: "", moveInDate: "", reasonForMoving: "" },
        emergencyContact: { name: "", relationship: "", phone: "", email: "" },
        status: "prospective",
        notes: "",
      });

      if (onTenantAdded) onTenantAdded(data.data);
      handleClose();
    } catch (err) {
      console.error("Error creating tenant:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!show) return null;

  return (
    <div style={ms.overlay} onClick={(e) => e.target === e.currentTarget && handleClose()}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      <div style={ms.modal}>

        {/* Header */}
        <div style={ms.modalHead}>
          <span style={ms.modalTitle}>Add New Tenant</span>
          <button style={ms.closeBtn} onClick={handleClose} disabled={loading}>×</button>
        </div>

        {/* Body */}
        <div style={ms.modalBody}>
          {error && <div style={ms.errorBanner}>{error}</div>}

          <form onSubmit={handleSubmit} id="add-tenant-form">

            {/* Personal Information */}
            <div style={ms.sectionHead}>Personal Information</div>
            <div className="re-stack-sm" style={{ ...ms.grid2, marginBottom: "1.25rem" }}>
              <Field label="First Name *">
                <input style={ms.input} type="text" required
                  value={formData.personalInfo.firstName}
                  onChange={(e) => set("personalInfo", "firstName", e.target.value)} />
              </Field>
              <Field label="Last Name *">
                <input style={ms.input} type="text" required
                  value={formData.personalInfo.lastName}
                  onChange={(e) => set("personalInfo", "lastName", e.target.value)} />
              </Field>
              <Field label="Email *">
                <input style={ms.input} type="email" required
                  value={formData.personalInfo.email}
                  onChange={(e) => set("personalInfo", "email", e.target.value)} />
              </Field>
              <Field label="Phone *">
                <input style={ms.input} type="tel" required
                  value={formData.personalInfo.phone}
                  onChange={(e) => set("personalInfo", "phone", e.target.value)} />
              </Field>
              <Field label="Date of Birth *">
                <input style={ms.input} type="date" required
                  value={formData.personalInfo.dateOfBirth}
                  onChange={(e) => set("personalInfo", "dateOfBirth", e.target.value)} />
              </Field>
            </div>

            {/* Employment Information */}
            <div style={ms.sectionHead}>Employment Information</div>
            <div className="re-stack-sm" style={{ ...ms.grid2, marginBottom: "1.25rem" }}>
              <Field label="Employer *">
                <input style={ms.input} type="text" required
                  value={formData.employment.employer}
                  onChange={(e) => set("employment", "employer", e.target.value)} />
              </Field>
              <Field label="Job Title *">
                <input style={ms.input} type="text" required
                  value={formData.employment.jobTitle}
                  onChange={(e) => set("employment", "jobTitle", e.target.value)} />
              </Field>
              <Field label="Monthly Income (TTD) *">
                <input style={ms.input} type="number" required min="0"
                  value={formData.employment.monthlyIncome}
                  onChange={(e) => set("employment", "monthlyIncome", e.target.value)} />
              </Field>
              <Field label="Employment Start Date *">
                <input style={ms.input} type="date" required
                  value={formData.employment.employmentStartDate}
                  onChange={(e) => set("employment", "employmentStartDate", e.target.value)} />
              </Field>
            </div>

            {/* Current Address */}
            <div style={ms.sectionHead}>Current Address</div>
            <div className="re-stack-sm" style={{ ...ms.grid2, marginBottom: "1.25rem" }}>
              <Field label="Street Address *" span2>
                <input style={ms.input} type="text" required
                  value={formData.currentAddress.street}
                  onChange={(e) => set("currentAddress", "street", e.target.value)} />
              </Field>
              <Field label="City *">
                <input style={ms.input} type="text" required
                  value={formData.currentAddress.city}
                  onChange={(e) => set("currentAddress", "city", e.target.value)} />
              </Field>
              <Field label="Area/Region *">
                <select style={ms.select} required
                  value={formData.currentAddress.parish}
                  onChange={(e) => set("currentAddress", "parish", e.target.value)}>
                  <option value="">Select Area/Region</option>
                  {TT_AREA_GROUPS.map((group) => (
                    <optgroup key={group.label} label={group.label}>
                      {group.areas.map((area) => (
                        <option key={area} value={area}>{area}</option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </Field>
              <Field label="Postal Code *">
                <input style={ms.input} type="text" required
                  value={formData.currentAddress.zipCode}
                  onChange={(e) => set("currentAddress", "zipCode", e.target.value)} />
              </Field>
              <Field label="Move-in Date *">
                <input style={ms.input} type="date" required
                  value={formData.currentAddress.moveInDate}
                  onChange={(e) => set("currentAddress", "moveInDate", e.target.value)} />
              </Field>
            </div>

            {/* Emergency Contact */}
            <div style={ms.sectionHead}>Emergency Contact</div>
            <div className="re-stack-sm" style={{ ...ms.grid2, marginBottom: "1.25rem" }}>
              <Field label="Name *">
                <input style={ms.input} type="text" required
                  value={formData.emergencyContact.name}
                  onChange={(e) => set("emergencyContact", "name", e.target.value)} />
              </Field>
              <Field label="Relationship *">
                <input style={ms.input} type="text" required
                  value={formData.emergencyContact.relationship}
                  onChange={(e) => set("emergencyContact", "relationship", e.target.value)} />
              </Field>
              <Field label="Phone *">
                <input style={ms.input} type="tel" required
                  value={formData.emergencyContact.phone}
                  onChange={(e) => set("emergencyContact", "phone", e.target.value)} />
              </Field>
              <Field label="Email">
                <input style={ms.input} type="email"
                  value={formData.emergencyContact.email}
                  onChange={(e) => set("emergencyContact", "email", e.target.value)} />
              </Field>
            </div>

            {/* Additional Information */}
            <div style={ms.sectionHead}>Additional Information</div>
            <div className="re-stack-sm" style={{ ...ms.grid2, marginBottom: "0.5rem" }}>
              <Field label="Status">
                <select style={ms.select}
                  value={formData.status}
                  onChange={(e) => setFormData((prev) => ({ ...prev, status: e.target.value }))}>
                  <option value="prospective">Prospective</option>
                  <option value="active">Active</option>
                  <option value="past">Past</option>
                </select>
              </Field>
              <Field label="Notes" span2>
                <textarea style={ms.textarea}
                  value={formData.notes}
                  placeholder="Any additional notes about this tenant..."
                  onChange={(e) => setFormData((prev) => ({ ...prev, notes: e.target.value }))} />
              </Field>
            </div>

          </form>
        </div>

        {/* Footer */}
        <div style={ms.modalFoot}>
          <button style={ms.btnSecondary} onClick={handleClose} disabled={loading}>
            Cancel
          </button>
          <button style={ms.btnPrimary} form="add-tenant-form" type="submit" onClick={handleSubmit} disabled={loading}>
            {loading ? (
              <>
                <span style={ms.spinner} />
                Adding…
              </>
            ) : (
              "Add Tenant"
            )}
          </button>
        </div>

      </div>
    </div>
  );
}

export default AddTenantModal;
